/**
 * Graph RAG client for the AI Cortex.
 *
 * Combines the existing vector-based document retriever with a
 * knowledge-graph query endpoint to produce hybrid retrieval results
 * with source attribution (decision D6).
 *
 * @module gibbs/cortex/graphRAG
 */

import { retrieveDocs } from '../text-to-action/docRetriever';

/** Backend endpoint for graph queries. */
let graphEndpoint = '/services/cortex/graph/query/';

/**
 * Override the graph endpoint (for testing).
 * @param {string} url
 */
export function setGraphEndpoint(url) {
    graphEndpoint = url;
}

/** Timeout per retrieval source in milliseconds. */
const RETRIEVAL_TIMEOUT_MS = 2000;

// ── Entity extraction helpers ─────────────────────────────────────

/**
 * Regex for PDB IDs — four alphanumeric characters (e.g. 1ABC).
 * @type {RegExp}
 */
const PDB_PATTERN = /\b[0-9][A-Za-z0-9]{3}\b/g;

/**
 * Common protein-name patterns (simple heuristic).
 * Matches words ending in -ase, -in, -or, or all-caps >=3 chars.
 * @type {RegExp}
 */
const PROTEIN_PATTERN = /\b[A-Z][a-z]*(?:ase|in|or)\b/g;

/**
 * Compound name pattern — CamelCase with digits (e.g. BMS-123456).
 * @type {RegExp}
 */
const COMPOUND_PATTERN = /\b[A-Z]{2,5}-?\d{3,7}\b/g;

/**
 * Extract entity mentions from a message for graph lookup.
 *
 * @param {string} message - User input text
 * @returns {{pdbIds: string[], proteins: string[], compounds: string[]}}
 */
export function extractEntities(message) {
    const pdbIds = [...new Set(
        (message.match(PDB_PATTERN) || [])
    )];
    const proteins = [...new Set(
        (message.match(PROTEIN_PATTERN) || [])
    )];
    const compounds = [...new Set(
        (message.match(COMPOUND_PATTERN) || [])
    )];
    return { pdbIds, proteins, compounds };
}

// ── Graph query ───────────────────────────────────────────────────

/**
 * Query the knowledge graph for an entity and its neighbours.
 *
 * @param {string} entityType - 'protein' | 'compound' | 'workflow' etc.
 * @param {string} entityId - Identifier for the entity
 * @param {number} [depth=1] - Traversal depth
 * @returns {Promise<Array<Object>>} Graph result nodes/edges
 */
export async function queryGraph(entityType, entityId, depth = 1) {
    const controller = new AbortController();
    const timer = setTimeout(
        () => controller.abort(),
        RETRIEVAL_TIMEOUT_MS
    );

    const params = new URLSearchParams({
        type: entityType,
        id: entityId,
        depth: String(depth),
    });

    try {
        const response = await fetch(
            `${graphEndpoint}?${params}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                signal: controller.signal,
            }
        );

        clearTimeout(timer);

        if (!response.ok) {
            console.warn(
                '[graphRAG] Graph endpoint returned'
                + ` HTTP ${response.status}`
            );
            return [];
        }

        const data = await response.json();
        return Array.isArray(data.results) ? data.results : [];
    } catch (err) {
        clearTimeout(timer);
        const isTimeout = err.name === 'AbortError';
        console.warn(
            `[graphRAG] ${isTimeout
                ? `Timed out after ${RETRIEVAL_TIMEOUT_MS}ms`
                : `Network error: ${err.message}`}`
        );
        return [];
    }
}

// ── Hybrid retrieval ──────────────────────────────────────────────

/**
 * Tag each result with a source label.
 *
 * @param {Array<Object>} results
 * @param {string} source - 'documentation' | 'institutional' | 'graph'
 * @returns {Array<Object>}
 */
function tagSource(results, source) {
    return results.map((r) => ({ ...r, source }));
}

/**
 * Merge two result arrays by interleaving based on score.
 *
 * Items are sorted by descending `score` and interleaved so that
 * higher-scored items from either source appear first.
 *
 * @param {Array<Object>} a
 * @param {Array<Object>} b
 * @returns {Array<Object>}
 */
function mergeByScore(a, b) {
    return [...a, ...b].sort(
        (x, y) => (y.score || 0) - (x.score || 0)
    );
}

/**
 * Perform hybrid retrieval: vector RAG + graph query in parallel.
 *
 * Each source has an independent 2-second timeout.  If one source
 * fails or times out, partial results from the other are returned
 * (decision D6 / hazard mitigation).
 *
 * @param {string} query - User query text
 * @param {string} domain - Active domain key
 * @returns {Promise<Array<Object>>} Merged results with source tags
 */
export async function hybridRetrieve(query, domain) {
    const entities = extractEntities(query);

    // Build graph query promise from extracted entities
    const graphPromise = (async () => {
        const allResults = [];
        const entityList = [
            ...entities.pdbIds.map((id) => ['protein', id]),
            ...entities.proteins.map((p) => ['protein', p]),
            ...entities.compounds.map((c) => ['compound', c]),
        ];

        const settled = await Promise.allSettled(
            entityList.map(([type, id]) => queryGraph(type, id))
        );

        for (const result of settled) {
            if (result.status === 'fulfilled') {
                allResults.push(...result.value);
            }
        }
        return tagSource(allResults, 'graph');
    })();

    // Vector retrieval via existing docRetriever
    const vectorPromise = retrieveDocs(query, domain)
        .then((chunks) => tagSource(chunks, 'documentation'));

    const [vectorResult, graphResult] = await Promise.allSettled([
        vectorPromise,
        graphPromise,
    ]);

    const vectorHits = vectorResult.status === 'fulfilled'
        ? vectorResult.value : [];
    const graphHits = graphResult.status === 'fulfilled'
        ? graphResult.value : [];

    return mergeByScore(vectorHits, graphHits);
}
